import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { MagnifyingGlassIcon, XCloseIcon } from '@rsos/base-assets/icons';
import { Text, Box, Button } from '@rsos/base-components';
import {
  SmartPhraseText,
  useSmartPhraseContext,
} from '@rsos/components/capstone/base/SmartPhrase';
import { useCurrentPSAP } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import { useSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { type PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import { selectUser, getTokens } from '@rsos/sinatra';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import {
  trackLocationRequestGeneratedFromPhrases,
  type LocationRequestPathway,
} from '../../../gainsightTrackings/locationRequestTrackings';
import {
  CloseButton,
  ListButton,
  MessageDisplay,
  SampleMessage,
  SearchBar,
  SearchInput,
  Titles,
} from './AllMessages.styles';
import {
  getDisplayableMessages,
  getMatchingPreconfigured,
  handleDynamicMessage,
  messageRequiresCallerId,
  type ResolvedPreconfiguredResponse,
} from './utils';

interface AllMessagesInterface {
  preconfiguredMessages?: PreconfiguredResponse[];
  onCopyToChat: (preconfiguredMessages: ResolvedPreconfiguredResponse) => void;
  onSendMessage: (preconfiguredMessages: ResolvedPreconfiguredResponse) => void;
  handleSelectMessage?: (preconfiguredMessage: PreconfiguredResponse) => void;
  onClose?: () => void;
  callerID?: string;
  isOutboundSMS?: boolean;
  isSendMessageDisabled?: boolean;
  /**
   * Pathway to attribute a location request link minted from this panel to.
   * Chats pass their ChatContext value; defaults to 'unknown' so a caller that
   * forgets shows up as a visible bucket rather than a wrong pathway.
   */
  pathwayType?: LocationRequestPathway;
}

const AllMessages = ({
  preconfiguredMessages = [],
  handleSelectMessage,
  onCopyToChat,
  onSendMessage,
  onClose,
  callerID: outboundCallerID = '',
  isOutboundSMS = false,
  isSendMessageDisabled = false,
  pathwayType = 'unknown',
}: AllMessagesInterface) => {
  const psap = useCurrentPSAP();
  const user = useSelector(selectUser);
  const orgName = useCurrentOrgName();
  const token = getTokens()?.token;

  const selectedCallerID = useSelectedCallerID() || '';
  // In the Outbound SMS composer the caller is the number typed into it, never
  // the globally selected call — otherwise a call open in the queue would leak
  // in and surface caller-ID-bound messages this composer can't actually send.
  const callerID = isOutboundSMS ? outboundCallerID : selectedCallerID;

  const smartPhraseContext = useSmartPhraseContext({
    psap,
    user,
    orgName,
    token,
    callerID,
  });

  const [selectedMessage, setSelectedMessage] =
    useState<PreconfiguredResponse>();
  const [searchedMessage, setSearchedMessage] = useState('');
  const [errorSource, setErrorSource] = useState<'copy' | 'send' | null>(null);

  if (!callerID) {
    // eslint-disable-next-line no-console
    console.debug(
      '[AllMessages] No callerID — messages requiring a selected call will be hidden.',
    );
  }

  const matchingPreconfigured = useMemo(
    () =>
      getMatchingPreconfigured(
        getDisplayableMessages(preconfiguredMessages),
        searchedMessage,
      ).filter(msg => callerID || !messageRequiresCallerId(msg)),
    [preconfiguredMessages, searchedMessage, callerID],
  );

  const handleClick = (preconfiguredMessage: PreconfiguredResponse) => {
    setSelectedMessage(preconfiguredMessage);

    if (handleSelectMessage) {
      handleSelectMessage(preconfiguredMessage);
    }
  };

  const handleMessageAction = async (
    onAction: (msg: ResolvedPreconfiguredResponse) => void,
    source: 'copy' | 'send',
  ) => {
    if (!selectedMessage) return;

    let resolvedMessage: ResolvedPreconfiguredResponse;
    try {
      resolvedMessage = await handleDynamicMessage(
        selectedMessage,
        smartPhraseContext,
      );
    } catch {
      setErrorSource(source);
      return;
    }

    // Resolving a message that carries the link phrase mints a token through the
    // location-share API exactly as the Request Location button does, so it is
    // reported the same way. Most messages carry no link and report nothing.
    // conversation_id is left to the sent event, which knows it; the token is
    // what ties the two together.
    trackLocationRequestGeneratedFromPhrases(resolvedMessage.resolvedPhrases, {
      pathwayType,
      callerID,
      isOutbound: isOutboundSMS,
    });

    onAction(resolvedMessage);
  };

  const handleCopyToChat = () => handleMessageAction(onCopyToChat, 'copy');
  const handleSendMessage = () => handleMessageAction(onSendMessage, 'send');

  useEffect(() => {
    setErrorSource(null);
  }, [selectedMessage]);

  return (
    <Box
      width="100%"
      height="100%"
      backgroundColor="background"
      padding="4px 16px 16px"
      borderRadius="5px"
    >
      <Box horizontal justify="space-between" align="center" marginBottom="8px">
        <Text color="primaryText">All Messages</Text>
        <CloseButton btnType="invisible" onClick={onClose}>
          <XCloseIcon />
        </CloseButton>
      </Box>
      <Box horizontal grow overflowY="auto">
        <Box flex="1" marginRight="8px">
          <SearchBar>
            <MagnifyingGlassIcon
              color="secondaryText"
              height="16px"
              width="16px"
            />
            <SearchInput
              type="text"
              placeholder="Search Messages"
              onChange={e => setSearchedMessage(e.target.value)}
            />
          </SearchBar>
          <Titles>
            {matchingPreconfigured.map(preconfiguredMessage => (
              <ListButton
                key={preconfiguredMessage.title}
                isSelected={
                  selectedMessage?.title === preconfiguredMessage.title
                }
                onClick={() => handleClick(preconfiguredMessage)}
              >
                <Text color="primaryText" size="normal">
                  {preconfiguredMessage.title}
                </Text>
              </ListButton>
            ))}
          </Titles>
        </Box>
        <MessageDisplay>
          <Text color="secondaryText" fontWeight="bold" size="small">
            Message Text
          </Text>
          {selectedMessage?.body && (
            <SampleMessage>
              <Text
                data-name="selected-message"
                size="normal"
                color="background"
              >
                <SmartPhraseText
                  text={selectedMessage.body}
                  context={smartPhraseContext}
                />
              </Text>
            </SampleMessage>
          )}
        </MessageDisplay>
      </Box>
      <Box marginTop="8px" horizontal justify="flex-end">
        {errorSource && (
          <Text
            size="small"
            color="heavyDanger"
            data-name="selected-message-error"
          >
            Something went wrong, please try again
          </Text>
        )}
        <Button
          noMargin
          marginRight="8px"
          onClick={handleCopyToChat}
          disabled={!selectedMessage}
          btnType={errorSource === 'copy' ? 'warning_tertiary' : 'tertiary'}
        >
          Copy to Chat
        </Button>
        <Button
          noMargin
          onClick={handleSendMessage}
          disabled={!selectedMessage || isSendMessageDisabled}
          btnType={errorSource === 'send' ? 'warning_primary' : 'primary'}
        >
          Send Message
        </Button>
      </Box>
    </Box>
  );
};

export default AllMessages;
